import React, { useState, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { useSelector } from 'react-redux';

const ProjectModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialData = null 
}) => {
  const currentUser = useSelector(state => state?.auth?.user);
  const [projectData, setProjectData] = useState({
    Name: '',
    Environment: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Reset form when modal opens or closes
  useEffect(() => {
    if (isOpen && initialData) {
      setProjectData({
        Name: initialData.projectName || '',
        Environment: initialData.environment || ''
      });
      setSearchTerm(initialData.environment || '');
    } else if (!isOpen) {
      // Reset form when modal closes
      setProjectData({ Name: '', Environment: '' });
      setSearchTerm('');
      setIsDropdownOpen(false);
    }
  }, [isOpen, initialData]);

  const environments = ['development', 'staging', 'production', 'demo'];

  const filteredEnvironments = environments.filter(env =>
    env.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(projectData, currentUser?.userId);
    onClose();
  };

  const handleSelectEnvironment = (env) => {
    setProjectData({ ...projectData, Environment: env });
    setSearchTerm(env);
    setIsDropdownOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="bg-gray-900 px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-medium text-white">
              {initialData ? 'Edit Project' : 'Create New Project'}
            </h3>
            <button
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Project Name Input */}
              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">Project Name</label>
                <input
                  type="text"
                  value={projectData.Name}
                  onChange={(e) => setProjectData({ ...projectData, Name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                  placeholder="Enter project name"
                />
              </div>

              {/* Environment Dropdown */}
              <div className="relative">
                <label className="text-gray-700 text-sm font-medium block mb-1">Environment</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setIsDropdownOpen(true);
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Search environment..."
                  />
                  <Search className="absolute right-2 top-2.5 text-gray-400" size={20} />
                </div>

                {/* Dropdown List */}
                {isDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg">
                    <ul className="py-1 max-h-48 overflow-auto">
                      {filteredEnvironments.map((env) => (
                        <li
                          key={env}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleSelectEnvironment(env)}
                        >
                          {env}
                        </li>
                      ))}
                      {filteredEnvironments.length === 0 && (
                        <li className="px-3 py-2 text-gray-500">No matches found</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md font-medium hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                {initialData ? 'Update Project' : 'Create Project'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;